import { useQueryClient } from "@tanstack/react-query";
import { Tooltip, message } from "antd";
import { apiHost } from "consts";
import bem from "easy-bem";
import CaseShell from "features/cases/blocks/CaseShell";
import SphereSelect from "features/cases/elements/SphereSelect";
import SphereTopicSelect from "features/cases/elements/SphereTopicSelect";
import { usePublish } from "features/cases/hooks/usePublish";
import { arePrimaryAttrsFilled } from "features/cases/utils";
import { useMarkOnboardingSeen, useOnboarding } from "features/onboarding";
import { useDataQuery } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import ModalWindow from "ui/ModalWindow";

import "./Problem.css";

const cn = bem("problem");

const collectivePersonId = 2;

interface OwnProps {
  problemId?: string;
  solutionId?: string;
  solsExpanded?: boolean;
  setSolsExpanded?: any;
  setProbExpanded?: (expanded: boolean) => void;
  probExpanded?: boolean;
  setCaseExpanded?: (expanded: boolean) => void;
  caseExpanded?: boolean;
  isNewProblemPage?: boolean;
}

const Problem: React.FC<OwnProps> = ({
  problemId,
  solutionId,
  setSolsExpanded,
  probExpanded,
  setProbExpanded,
  setCaseExpanded,
  caseExpanded = false,
  isNewProblemPage = false,
}) => {
  const problemAttrs: any[] = useDataQuery(
    "attrs/?attributeOf=problem",
    isNewProblemPage,
    [],
  );

  const attrs = problemAttrs;

  const attrCards = useDataQuery(
    `problems/${problemId}/cards/`,
    isNewProblemPage,
    [],
  );

  const primaryAttrsFilled = arePrimaryAttrsFilled(attrs, attrCards);
  const disabled = !primaryAttrsFilled;

  const problem = useDataQuery(`problems/${problemId}`, isNewProblemPage);

  const [selectedSpheres, setSpheres] = useState<number[]>(
    problem?.spheres || problem?.sphere || [],
  );
  const [selectedTopics, setTopics] = useState<number[]>(problem?.topics || []);

  const markOnboardingSeen = useMarkOnboardingSeen();
  const refSaveButton = useRef(null);
  const refSphereInput = useRef(null);

  const { tourNode: tourFirstCards, seen: firstCardsSeen } = useOnboarding({
    topic: "problem-cards-first",
    refs: [undefined, refSphereInput, refSaveButton],
    condition: attrCards?.length === 0 && !!problem?.title,
    onOpen: () => markOnboardingSeen("reading"),
  });

  const { tourNode: tourPrimary } = useOnboarding({
    topic: "problem-cards-primary",
    refs: [undefined, refSphereInput, refSaveButton],
    condition: !primaryAttrsFilled && attrCards?.length === 1 && firstCardsSeen,
    onOpen: () => markOnboardingSeen("reading"),
  });

  const {
    isEditor,
    isAdmin,
    isContributor,
    isContentEditor,
    userId,
    isBannedFromPublishing,
    canPublishAsCollective,
  } = useUser();
  const topicsTest = useFlag("topicsTest");
  const showTopicPicker = isContentEditor || isAdmin || topicsTest;
  const queryClient = useQueryClient();

  const contentMeta = problem?.meta || {};
  const isMyStuff = userId && contentMeta.user === userId;
  const isCollectiveContent = contentMeta.author === collectivePersonId;
  // Коллективным контентом можно управлять только при действующем праве
  // публиковать от имени коллективного разума; одного авторства недостаточно.
  const canActOnOwnContent =
    isMyStuff && (!isCollectiveContent || canPublishAsCollective);
  const canPublish =
    isAdmin || (isContributor && canActOnOwnContent && !isBannedFromPublishing);

  const navigate = useNavigate();
  const spheres = useDataQuery<{ id: number; slug?: string }[]>("spheres");

  // После публикации: сообщение + переход в каталог, отфильтрованный по сфере
  // проблемы, со свежими сверху и подсветкой только что опубликованной карточки.
  const onPublishSuccess = useCallback(
    (val: number) => {
      if (val !== 1) return;

      message.success("Проблема опубликована");

      const firstSphereId = selectedSpheres[0];
      const sphere = spheres?.find((s) => s.id === firstSphereId);
      const slug =
        sphere?.slug || (firstSphereId ? `sphere-${firstSphereId}` : undefined);

      navigate({
        pathname: "/problems",
        search: slug
          ? `?sphere=${slug}&sort=-dateModified`
          : `?sort=-dateModified`,
        hash: `#problem-${problemId}`,
      });
    },
    [selectedSpheres, spheres, navigate, problemId],
  );

  const onPublishClick = usePublish(
    (val) =>
      `${apiHost}/solutions/api/problems/${problemId}/publish/${val}?spheres=${selectedSpheres.join(",")}&topics=${selectedTopics.join(",")}`,
    `problems/${problemId}`,
    onPublishSuccess,
  );

  const onListClick = usePublish(
    (val) =>
      `${apiHost}/solutions/api/problems/${problemId}/set_is_listed/${val}`,
    `problems/${problemId}`,
  );

  const [isPublishModalVisible, setIsPublishModalVisible] = useState(false);

  const onGoPublishClick = useCallback(() => {
    setIsPublishModalVisible(true);
  }, []);

  const publishModal = useMemo(
    () => (
      <ModalWindow
        title="Публикация проблемы"
        content={
          <div
            className={cn("publish-modal-content", { tall: showTopicPicker })}
          >
            <p>Для публикации проблемы выберите одну или несколько сфер:</p>
            {showTopicPicker ? (
              <SphereTopicSelect
                selectedSphereIds={selectedSpheres}
                selectedTopicIds={selectedTopics}
                onChange={({ sphereIds, topicIds }) => {
                  setSpheres(sphereIds);
                  setTopics(topicIds);
                }}
              />
            ) : (
              <SphereSelect
                selectedIds={selectedSpheres}
                onChange={setSpheres}
              />
            )}
            <div className={cn("publish-modal-spacer")} />
            <div className={cn("publish-modal-actions")}>
              <button onClick={() => setIsPublishModalVisible(false)}>
                Отмена
              </button>
              <button
                disabled={disabled}
                onClick={() => {
                  onPublishClick(1);
                  setIsPublishModalVisible(false);
                }}
              >
                Опубликовать
              </button>
            </div>
          </div>
        }
        isModalOpen={isPublishModalVisible}
        setIsModalOpen={setIsPublishModalVisible}
      />
    ),
    [
      isPublishModalVisible,
      disabled,
      onPublishClick,
      problem,
      selectedSpheres,
      selectedTopics,
      showTopicPicker,
    ],
  );

  const [isTaxonomyModalVisible, setIsTaxonomyModalVisible] = useState(false);
  const [taxonomySpheres, setTaxonomySpheres] = useState<number[]>([]);
  const [taxonomyTopics, setTaxonomyTopics] = useState<number[]>([]);

  const onGoEditTaxonomyClick = useCallback(() => {
    setTaxonomySpheres(problem?.spheres || []);
    setTaxonomyTopics(problem?.topics || []);
    setIsTaxonomyModalVisible(true);
  }, [problem]);

  const onSaveTaxonomyClick = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiHost}/solutions/api/problems/${problemId}/edit/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            spheres: taxonomySpheres,
            topics: taxonomyTopics,
          }),
          credentials: "include",
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      await queryClient.refetchQueries({ queryKey: [`problems/${problemId}`] });
      message.success("Сферы и темы обновлены");
      setIsTaxonomyModalVisible(false);
    } catch {
      message.error("Не удалось сохранить сферы и темы");
    }
  }, [taxonomySpheres, taxonomyTopics, problemId, queryClient]);

  const taxonomyModal = useMemo(
    () => (
      <ModalWindow
        title="Сферы и темы"
        content={
          <div className={cn("publish-modal-content", { tall: true })}>
            <SphereTopicSelect
              selectedSphereIds={taxonomySpheres}
              selectedTopicIds={taxonomyTopics}
              onChange={({ sphereIds, topicIds }) => {
                setTaxonomySpheres(sphereIds);
                setTaxonomyTopics(topicIds);
              }}
            />
            <div className={cn("publish-modal-spacer")} />
            <div className={cn("publish-modal-actions")}>
              <button onClick={() => setIsTaxonomyModalVisible(false)}>
                Отмена
              </button>
              <button onClick={onSaveTaxonomyClick}>Сохранить</button>
            </div>
          </div>
        }
        isModalOpen={isTaxonomyModalVisible}
        setIsModalOpen={setIsTaxonomyModalVisible}
      />
    ),
    [
      isTaxonomyModalVisible,
      taxonomySpheres,
      taxonomyTopics,
      onSaveTaxonomyClick,
    ],
  );

  if (!problem && !isNewProblemPage) {
    return <div>Problem not found.</div>;
  }

  if (!isNewProblemPage && (problem.meta.isDeleted || problem.meta.isDraft)) {
    if (!isMyStuff && !isEditor && !isAdmin) {
      return <div>Problem not found or unavailable.</div>;
    }
  }

  const footer = (
    <>
      {canPublish && (
        <span>
          {problem?.meta?.isDraft ? (
            <Tooltip
              placement="bottomRight"
              title={
                disabled
                  ? "Нельзя опубликовать материалы по проблеме пока не заполнены главные атрибуты"
                  : ""
              }
            >
              <button
                disabled={disabled}
                onClick={onGoPublishClick}
              >
                Опубликовать
              </button>
            </Tooltip>
          ) : // Временно отключено:
          // isAdmin ? (
          //   <button onClick={() => onPublishClick(0)}>
          //     Скрыть все материалы в черновики
          //   </button>
          // ) : null
          null}{" "}
          {isAdmin &&
            (problem?.isListed ? (
              <button onClick={() => onListClick(0)}>
                Скрыть проблему из списка
              </button>
            ) : (
              <Tooltip
                placement="bottomRight"
                title={
                  disabled
                    ? "Нельзя показать проблему пока не заполнены главные атрибуты"
                    : ""
                }
              >
                <button
                  disabled={disabled}
                  onClick={() => onListClick(1)}
                >
                  Показывать проблему в списке
                </button>
              </Tooltip>
            ))}
        </span>
      )}
      {isContentEditor && !problem?.meta?.isDraft && (
        <button onClick={onGoEditTaxonomyClick}>Сферы и темы</button>
      )}
    </>
  );

  return (
    <>
      <CaseShell
        kind="problem"
        problemId={problemId}
        solutionId={solutionId}
        isAdding={isNewProblemPage}
        initialScroll="top"
        setSolsExpanded={setSolsExpanded}
        probExpanded={probExpanded}
        setProbExpanded={setProbExpanded}
        setCaseExpanded={setCaseExpanded}
        caseExpanded={caseExpanded}
        onboardingNodes={
          <>
            {isContributor && tourFirstCards}
            {isContributor && tourPrimary}
          </>
        }
        footer={footer}
      />
      {publishModal}
      {taxonomyModal}
    </>
  );
};

export default Problem;
